import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import { Button, Card, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    maxWidth: "800px",
    margin: "0 auto",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(3),
  },
  printButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  reportWrapper: {
    width: "100%",
    fontFamily: "Arial, sans-serif",
    "& @media print": {
      width: "80mm",
      margin: "0 5mm",
    },
  },
  headerText: {
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  divider: {
    borderBottom: "2px dotted #000",
    margin: "12px 0",
  },
  dataText: {
    fontSize: "14px",
    lineHeight: "1.5",
    "& > div": {
      marginBottom: "4px",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: theme.spacing(2),
    "& th": {
      fontSize: "16px",
      padding: "8px 0",
      borderBottom: "1px solid #ddd",
    },
    "& td": {
      fontSize: "14px",
      padding: "8px 0",
    },
  },
  summaryText: {
    fontSize: "15px",
    "& > div": {
      margin: "8px 0",
    },
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    margin: "16px 0 8px 0",
  },
  footer: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "14px",
    marginTop: theme.spacing(2),
  },
}));

const ThermalSalesReport = ({ userProfile, salesData, selectedDate, startDate, endDate }) => {
  //   const [selectedDate, setSelectedDate] = useState(new Date());
  const classes = useStyles();
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    // Filter invoices for selected date range or single date, excluding complimentary tickets
    let filtered;
    
    if (startDate && endDate) {
      // Use date range filtering
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).endOf('day');
      
      filtered = salesData.invoices.filter((invoice) => {
        const invoiceDate = moment(invoice.createdAt);
        const isInDateRange = invoiceDate.isBetween(start, end, null, '[]'); // inclusive of both start and end
        const isNotComplimentary = !invoice.isComplimentary; // Exclude complimentary tickets
        return isInDateRange && isNotComplimentary;
      });
    } else {
      // Fallback to single date filtering
      filtered = salesData.invoices.filter(
        (invoice) =>
          moment(invoice.createdAt).format("YYYY-MM-DD") ===
          moment(selectedDate).format("YYYY-MM-DD") &&
          !invoice.isComplimentary // Exclude complimentary tickets
      );
    }
    
    console.log("filtered", filtered);
    setFilteredInvoices(filtered);
  }, [selectedDate, startDate, endDate, salesData.invoices]);

  // Calculate total invoices and complimentary invoices for the selected date range
  const getInvoiceCounts = () => {
    let allInvoices = [];
    
    if (startDate && endDate) {
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).endOf('day');
      
      allInvoices = salesData.invoices.filter((invoice) => {
        const invoiceDate = moment(invoice.createdAt);
        return invoiceDate.isBetween(start, end, null, '[]');
      });
    } else {
      allInvoices = salesData.invoices.filter(
        (invoice) =>
          moment(invoice.createdAt).format("YYYY-MM-DD") ===
          moment(selectedDate).format("YYYY-MM-DD")
      );
    }
    
    const regularInvoices = allInvoices.filter(invoice => !invoice.isComplimentary);
    const complimentaryInvoices = allInvoices.filter(invoice => invoice.isComplimentary);
    
    return {
      total: allInvoices.length,
      regular: regularInvoices.length,
      complimentary: complimentaryInvoices.length
    };
  };

  const invoiceCounts = getInvoiceCounts();

  // Get starting and ending invoice numbers within the selected date (or range)
  const getInvoiceNumberRange = () => {
    let allInvoices = [];
    if (startDate && endDate) {
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).endOf('day');
      allInvoices = salesData.invoices.filter((invoice) => {
        const invoiceDate = moment(invoice.createdAt);
        return invoiceDate.isBetween(start, end, null, '[]');
      });
    } else {
      allInvoices = salesData.invoices.filter(
        (invoice) =>
          moment(invoice.createdAt).format('YYYY-MM-DD') ===
          moment(selectedDate).format('YYYY-MM-DD')
      );
    }

    if (!allInvoices || allInvoices.length === 0) return { start: '-', end: '-' };

    // Prefer numeric comparison if possible
    const parseNum = (v) => {
      const n = parseInt(String(v), 10);
      return Number.isNaN(n) ? null : n;
    };

    const withNum = allInvoices
      .map((inv) => ({ inv, num: parseNum(inv.invoiceNumber) }))
      .filter((x) => x.inv && x.inv.invoiceNumber);

    let startInv = '-';
    let endInv = '-';

    if (withNum.length > 0 && withNum.some((x) => x.num !== null)) {
      // Use numeric sorting where available; fallback keep originals otherwise
      const numericOnly = withNum.filter((x) => x.num !== null);
      numericOnly.sort((a, b) => a.num - b.num);
      startInv = numericOnly[0]?.inv?.invoiceNumber || '-';
      endInv = numericOnly[numericOnly.length - 1]?.inv?.invoiceNumber || '-';
    } else {
      // Fallback to createdAt order
      const byDate = [...allInvoices].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      startInv = byDate[0]?.invoiceNumber || '-';
      endInv = byDate[byDate.length - 1]?.invoiceNumber || '-';
    }

    return { start: startInv, end: endInv };
  };

  const invoiceNumberRange = getInvoiceNumberRange();

  //   const handleDateChange = (date) => {
  //     setSelectedDate(date);
  //   };

  const handlePrint = () => {
    const printContent = document.getElementById("thermal-sales-report");

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.write(
      "<!DOCTYPE html><html><head><title>Sales Report</title>" +
        "<style>" +
        "@page { size: 80mm auto; margin: 0mm 5mm 0mm 5mm; }" +
        "@media print {" +
        "  body { margin: 0; padding: 0; width: 80mm; }" +
        '  .report-wrapper { width: 72mm; padding: 2mm 4mm; font-family: "Arial", sans-serif; }' +
        "  .text-center { text-align: center; }" +
        "  .text-right { text-align: right; }" +
        "  .bold { font-weight: bold; }" +
        "  .divider { border-bottom: 1px dotted #000; margin: 5px 0; }" +
        "  table { width: 100%; border-collapse: collapse; }" +
        "  th, td { padding: 2px 0; }" +
        "  .header-text { font-size: 14px; }" +
        "  .data-text { font-size: 12px; }" +
        "  .summary-text { font-size: 13px; }" +
        "}" +
        "</style></head><body>" +
        printContent.innerHTML +
        "</body></html>"
    );
    iframeDoc.close();

    iframe.contentWindow.onafterprint = () => {
      document.body.removeChild(iframe);
    };

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  // Calculate item-wise totals based on raw DB price (qty × units.sale.price), without discount apportionment
  const itemTotals = filteredInvoices.reduce((acc, invoice) => {
    const hasUncategorized = invoice.items.some(
      (item) => !item?.item?.category?.categoryName
    );

    // Only process if no uncategorized items
    if (!hasUncategorized) {
      invoice.items.forEach((item) => {
        const itemName = item?.item?.itemName || "Uncategorized";
        const category = item?.item?.category?.categoryName || "Uncategorized";

        if (category !== "Uncategorized") {
          if (!acc[itemName]) {
            acc[itemName] = {
              total: 0,
              quantity: 0,
              category: category,
            };
          }
          const itemValue = item.quantity * (item?.item?.units?.sale?.price || 0);
          acc[itemName].total += itemValue;
          acc[itemName].quantity += item.quantity;
        }
      });
    }
    return acc;
  }, {});


  // Sort items by category and then by name
  const sortedItems = Object.entries(itemTotals).sort((a, b) => {
    if (a[1].category === b[1].category) {
      return a[0].localeCompare(b[0]);
    }
    return a[1].category.localeCompare(b[1].category);
  });

  // Separate old and new invoices for clear calculation
  const oldInvoices = filteredInvoices.filter(invoice => {
    const hasUncategorized = invoice.items.some(
      (item) => !item?.item?.category?.categoryName
    );
    return !hasUncategorized && !(invoice.adjustedSubTotal || invoice.adjustedVat);
  });

  const newInvoices = filteredInvoices.filter(invoice => {
    const hasUncategorized = invoice.items.some(
      (item) => !item?.item?.category?.categoryName
    );
    return !hasUncategorized && (invoice.adjustedSubTotal || invoice.adjustedVat);
  });

  // Calculate totals for old invoices (traditional calculation)
  const oldInvoicesSales = Number(
    oldInvoices
      .reduce((acc, invoice) => {
        const subTotal = invoice.subTotal || 
          (invoice.total - (invoice.vat || 0) - (parseFloat(String(invoice?.roundoff || 0).trim()) || 0));
        return acc + subTotal;
      }, 0)
      .toFixed(2)
  );

  const oldInvoicesVat = Number(
    oldInvoices
      .reduce((acc, invoice) => acc + (invoice.vat || 0), 0)
      .toFixed(2)
  );

  const oldInvoicesRoundoff = Number(
    oldInvoices
      .reduce((acc, invoice) => {
        const roundoff = parseFloat(String(invoice?.roundoff || 0).trim()) || 0;
        return acc + roundoff;
      }, 0)
      .toFixed(2)
  );

  const oldInvoicesDiscount = Number(
    oldInvoices
      .reduce((acc, invoice) => acc + (invoice.discount || 0), 0)
      .toFixed(2)
  );

  // Calculate totals for new invoices using database fields directly
  const newInvoicesSales = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.originalSubTotal || 0), 0)
      .toFixed(2)
  );

  const newInvoicesVat = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.originalVat || 0), 0)
      .toFixed(2)
  );

  const newInvoicesRoundoff = Number(
    newInvoices
      .reduce((acc, invoice) => {
        const roundoff = parseFloat(String(invoice?.roundoff || 0).trim()) || 0;
        return acc + roundoff;
      }, 0)
      .toFixed(2)
  );

  const newInvoicesDiscount = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.discount || 0), 0)
      .toFixed(2)
  );

  // Calculate combined totals - use original sub-totals for both old and new invoices
  const totalSales = Number((oldInvoicesSales + newInvoicesSales).toFixed(2));
  const totalVat = Number((oldInvoicesVat + newInvoicesVat).toFixed(2));
  const totalRoundoff = Number((oldInvoicesRoundoff + newInvoicesRoundoff).toFixed(2));
  const totalDiscount = Number((oldInvoicesDiscount + newInvoicesDiscount).toFixed(2));
  const netSales = Number(
    filteredInvoices
      .reduce((acc, invoice) => {
        const hasUncategorized = invoice.items.some(
          (item) => !item?.item?.category?.categoryName
        );
        return hasUncategorized ? acc : acc + (invoice.total || 0);
      }, 0)
      .toFixed(2)
  );

  // Calculate new GST compliance totals using database fields directly
  const totalAdjustedSubTotal = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.adjustedSubTotal || 0), 0)
      .toFixed(2)
  );

  const totalAdjustedVat = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.adjustedVat || 0), 0)
      .toFixed(2)
  );

  const totalRoundoffSubTotalAdj = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.roundoffSubTotalAdj || 0), 0)
      .toFixed(2)
  );

  const totalRoundoffGstAdj = Number(
    newInvoices
      .reduce((acc, invoice) => acc + (invoice.roundoffGstAdj || 0), 0)
      .toFixed(2)
  );

  // Check if any invoices have new GST compliance fields
  const hasNewGstFields = newInvoices.length > 0;

  const toCommas = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  

  return (
    <Card className={classes.card}>
      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          onClick={handlePrint}
          className={classes.printButton}
        >
          Print Report
        </Button>
      </div>

      <div style={{ display: "none" }}>
        <div
          id="thermal-sales-report"
          className="report-wrapper"
          ref={printRef}
        >
          <div className="text-center bold header-text">Daily Sales Report</div>
          <div className="text-center bold header-text">
            {userProfile.businessName}
          </div>

          <div className="divider" />

          <div className="data-text">
            <div>
              {startDate && endDate 
                ? `Date Range: ${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`
                : `Date: ${moment(selectedDate).format("DD/MM/YYYY")}`
              }
            </div>
            <div>Printed On: {moment().format("DD/MM/YYYY HH:mm:ss")}</div>
            <div>
              Invoices: {invoiceCounts.regular} + {invoiceCounts.complimentary} complimentary = {invoiceCounts.total}
              {invoiceCounts.total > 0 && (
                <> | Range: {invoiceNumberRange.start} - {invoiceNumberRange.end}</>
              )}
            </div>
          </div>

          <div className="divider" />

          <div className="bold header-text">Item-wise Sales</div>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }} className="header-text">
                  Item
                </th>
                <th style={{ textAlign: "center" }} className="header-text">
                  Qty
                </th>
                <th style={{ textAlign: "right" }} className="header-text">
                  Amount(₹)
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map(([itemName, data]) => (
                <tr key={itemName} className="data-text">
                  <td style={{ textAlign: "left" }}>{itemName}</td>
                  <td style={{ textAlign: "center" }}>{data.quantity}</td>
                  <td style={{ textAlign: "right" }}>
                    {toCommas(data.total.toFixed(2))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="divider" />

          <div className="summary-text">
            {/* Sub-Total Section */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Sub-Total</span>
              <span>₹{toCommas((totalSales.toFixed(2)))}</span>
            </div>
            
            {/* Show Round-off Sub-Total Adjustment if there are adjustments */}
            {Math.abs(totalRoundoffSubTotalAdj) > 0.01 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginLeft: "10px", fontSize: "0.9em" }}>
                <span>Round-off Sub-Total Adj</span>
                <span style={{ color: totalRoundoffSubTotalAdj >= 0 ? "#008000" : "#ff0000" }}>
                  {totalRoundoffSubTotalAdj >= 0 ? "+" : ""}₹{toCommas((totalRoundoffSubTotalAdj.toFixed(2)))}
                </span>
              </div>
            )}
            
            {/* Adjusted Sub-Total from database */}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", marginTop: "5px", borderTop: "1px solid #ddd", paddingTop: "5px" }}>
              <span>Adjusted Sub-Total</span>
              <span>₹{toCommas((totalAdjustedSubTotal.toFixed(2)))}</span>
            </div>

            {/* GST Section */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
              <span>Total GST</span>
              <span>₹{toCommas((totalVat.toFixed(2)))}</span>
            </div>
            
            {/* Show GST Adjustments if there are any */}
            {Math.abs(totalRoundoffGstAdj) > 0.01 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginLeft: "10px", fontSize: "0.9em" }}>
                <span>Round-off GST Adj</span>
                <span style={{ color: totalRoundoffGstAdj >= 0 ? "#008000" : "#ff0000" }}>
                  {totalRoundoffGstAdj >= 0 ? "+" : ""}₹{toCommas((totalRoundoffGstAdj.toFixed(2)))}
                </span>
              </div>
            )}
            
            {/* Effective GST from database */}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", marginTop: "5px", borderTop: "1px solid #ddd", paddingTop: "5px" }}>
              <span>Effective GST</span>
              <span>₹{toCommas((totalAdjustedVat.toFixed(2)))}</span>
            </div>

            {/* Round-off Section */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
              <span>Total Roundoff</span>
              <span>₹{toCommas((totalRoundoff.toFixed(2)))}</span>
            </div>

            {/* Discount Section */}
            {totalDiscount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                <span>Total Discount</span>
                <span style={{ color: "#e74c3c" }}>
                  -₹{toCommas((totalDiscount.toFixed(2)))}
                </span>
              </div>
            )}

            {/* Divider before final total */}
            <div className="divider" style={{ margin: "15px 0" }} />

            {/* Final Net Sales */}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "16px" }}>
              <span>Net Sales</span>
              <span>
                ₹{toCommas((totalAdjustedSubTotal  + totalAdjustedVat ).toFixed(2))}
              </span>
            </div>

            {/* Show breakdown by invoice type if mixed */}
            {/* {oldInvoices.length > 0 && newInvoices.length > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px", paddingTop: "10px", borderTop: "1px dashed #ccc" }}>
                  <span style={{ fontWeight: "bold", fontSize: "0.9em" }}>Breakdown by Invoice Type:</span>
                  <span></span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginLeft: "10px", fontSize: "0.8em" }}>
                  <span>Old Invoices ({oldInvoices.length}):</span>
                  <span>₹{toCommas((oldInvoicesSales.toFixed(2)))}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginLeft: "10px", fontSize: "0.8em" }}>
                  <span>New Invoices ({newInvoices.length}):</span>
                  <span>₹{toCommas((newInvoicesSales.toFixed(2)))}</span>
                </div>
              </>
            )} */}
          </div>
          {/* <div className="bold header-text">Payment Mode Summary</div>
          {Object.entries(paymentTotals).map(([mode, amount]) => (
            <div
              key={mode}
              className="summary-text"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{mode}</span>
              <span>₹{amount.toFixed(2)}</span>
            </div>
          ))} */}

          <div className="divider" />

          <div className="text-center bold">*** End of Report ***</div>
        </div>
      </div>

      <div
        id="thermal-sales-report1"
        className={classes.reportWrapper}
        ref={printRef}
      >
        <div className={classes.headerText}>Daily Sales Report</div>
        <div className={classes.headerText}>{userProfile.businessName}</div>

        <div className={classes.divider} />

        <div className={classes.dataText}>
          <div>
            {startDate && endDate 
              ? `Date Range: ${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`
              : `Date: ${moment(selectedDate).format("DD/MM/YYYY")}`
            }
          </div>
          <div>Printed On: {moment().format("DD/MM/YYYY HH:mm:ss")}</div>
          <div>
            Invoices: {invoiceCounts.regular} + {invoiceCounts.complimentary} complimentary = {invoiceCounts.total}
            {invoiceCounts.total > 0 && (
              <> | Range: {invoiceNumberRange.start} - {invoiceNumberRange.end}</>
            )}
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.sectionTitle}>Item-wise Sales</div>
        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Item</th>
              <th style={{ textAlign: "center" }}>Qty</th>
              <th style={{ textAlign: "right" }}>Amount(₹)</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(([itemName, data]) => (
              <tr key={itemName}>
                <td style={{ textAlign: "left" }}>{itemName}</td>
                <td style={{ textAlign: "center" }}>{data.quantity}</td>
                <td style={{ textAlign: "right" }}>{toCommas(data.total.toFixed(2))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.divider} />

        <div className={classes.summaryText}>
          {/* Sub-Total Section */}
          <div className={classes.flexBetween}>
            <span>Sub-Total</span>
            <span>₹{toCommas((totalSales.toFixed(2)))}</span>
          </div>
          
          {/* Show Round-off Sub-Total Adjustment if there are adjustments */}
          {Math.abs(totalRoundoffSubTotalAdj) > 0.01 && (
            <div className={classes.flexBetween} style={{ marginLeft: "10px", fontSize: "0.9em" }}>
              <span>Round-off Sub-Total Adj</span>
              <span style={{ color: totalRoundoffSubTotalAdj >= 0 ? "#008000" : "#ff0000" }}>
                {totalRoundoffSubTotalAdj >= 0 ? "+" : ""}₹{toCommas((totalRoundoffSubTotalAdj.toFixed(2)))}
              </span>
            </div>
          )}
          
          {/* Adjusted Sub-Total from database */}
          <div className={classes.flexBetween} style={{ fontWeight: "bold", marginTop: "5px", borderTop: "1px solid #ddd", paddingTop: "5px" }}>
            <span>Adjusted Sub-Total</span>
            <span>₹{toCommas((totalAdjustedSubTotal.toFixed(2)))}</span>
          </div>

          {/* GST Section */}
          <div className={classes.flexBetween} style={{ marginTop: "10px" }}>
            <span>Total GST</span>
            <span>₹{toCommas((totalVat.toFixed(2)))}</span>
          </div>
          
          {/* Show GST Adjustments if there are any */}
          {Math.abs(totalRoundoffGstAdj) > 0.01 && (
            <div className={classes.flexBetween} style={{ marginLeft: "10px", fontSize: "0.9em" }}>
              <span>Round-off GST Adj</span>
              <span style={{ color: totalRoundoffGstAdj >= 0 ? "#008000" : "#ff0000" }}>
                {totalRoundoffGstAdj >= 0 ? "+" : ""}₹{toCommas((totalRoundoffGstAdj.toFixed(2)))}
              </span>
            </div>
          )}
          
          {/* Effective GST from database */}
          <div className={classes.flexBetween} style={{ fontWeight: "bold", marginTop: "5px", borderTop: "1px solid #ddd", paddingTop: "5px" }}>
            <span>Effective GST</span>
            <span>₹{toCommas((totalAdjustedVat.toFixed(2)))}</span>
          </div>

          {/* Round-off Section */}
          <div className={classes.flexBetween} style={{ marginTop: "10px" }}>
            <span>Total Roundoff</span>
            <span>₹{toCommas((totalRoundoff.toFixed(2)))}</span>
          </div>

          {/* Discount Section */}
          {totalDiscount > 0 && (
            <div className={classes.flexBetween} style={{ marginTop: "10px" }}>
              <span>Total Discount</span>
              <span style={{ color: "#e74c3c" }}>
                -₹{toCommas((totalDiscount.toFixed(2)))}
              </span>
            </div>
          )}

          {/* Divider before final total */}
          <div className={classes.divider} style={{ margin: "15px 0" }} />

          {/* Final Net Sales */}
          <div className={classes.flexBetween} style={{ fontWeight: "bold", fontSize: "16px" }}>
            <span>Net Sales</span>
            <span>
              ₹{toCommas((totalAdjustedSubTotal  + totalAdjustedVat ).toFixed(2))}
            </span>
          </div>

          {/* Show breakdown by invoice type if mixed */}
          {/* {oldInvoices.length > 0 && newInvoices.length > 0 && (
            <>
              <div className={classes.flexBetween} style={{ marginTop: "15px", paddingTop: "10px", borderTop: "1px dashed #ccc" }}>
                <span style={{ fontWeight: "bold", fontSize: "0.9em" }}>Breakdown by Invoice Type:</span>
                <span></span>
              </div>
              <div className={classes.flexBetween} style={{ marginLeft: "10px", fontSize: "0.8em" }}>
                <span>Old Invoices ({oldInvoices.length}):</span>
                <span>₹{toCommas((oldInvoicesSales.toFixed(2)))}</span>
              </div>
              <div className={classes.flexBetween} style={{ marginLeft: "10px", fontSize: "0.8em" }}>
                <span>New Invoices ({newInvoices.length}):</span>
                <span>₹{toCommas((newInvoicesSales.toFixed(2)))}</span>
              </div>
            </>
          )} */}
        </div>

        {/* <div className={classes.sectionTitle}>Payment Mode Summary</div>
        {Object.entries(paymentTotals).map(([mode, amount]) => (
          <div key={mode} className={classes.flexBetween}>
            <span>{mode}</span>
            <span>₹{amount.toFixed(2)}</span>
          </div>
        ))} */}

        <div className={classes.divider} />

        <div className={classes.footer}>*** End of Report ***</div>
      </div>
    </Card>
  );
};

export default ThermalSalesReport;
